import type { TrapetsMode, TrapetsSettingsUpdateRequest } from "@repo/shared/types";
import { useState, useTransition } from "react";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { patch } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { FeatureCard, type PriceLine } from "../third-party/feature-card";
import { T } from "./trapets-settings.i18n";

const PRICES: PriceLine[] = [
  { label: "Sanction List", unit: "Per month / unique screened entity", price: "0.200 EUR" },
  { label: "Nordic PEP Edge", unit: "Per month / unique screened entity", price: "0.255 EUR" },
];

type TrapetsSettingsProps = {
  trapetsMode: TrapetsMode;
  mutate: () => void;
};

export function TrapetsSettings({ trapetsMode: initialMode, mutate }: TrapetsSettingsProps) {
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mode, setMode] = useState<TrapetsMode>(initialMode);

  const modeHint =
    mode === "disabled"
      ? t.usingDisabled
      : mode === "production"
        ? t.usingProdCreds
        : t.usingTestCreds;

  // Auto-save on change — no save button. Revert the toggle if the write is rejected
  // (e.g. the disable-guard refuses while a flow still uses the component).
  const handleModeChange = (value: string) => {
    if (!value || value === mode) return;
    const previous = mode;
    setMode(value as TrapetsMode);
    setError(null);
    setSuccess(null);

    startTransition(async () => {
      try {
        await patch("/internal/trapets", {
          trapetsMode: value as TrapetsMode,
        } satisfies TrapetsSettingsUpdateRequest);
        setSuccess(t.settingsSaved);
        mutate();
      } catch (err: any) {
        setMode(previous);
        setError(err.message || t.failedToSave);
      }
    });
  };

  return (
    <FeatureCard
      title={t.title}
      description={t.description}
      prices={PRICES}
      vendor={{
        name: "Trapets AB",
        homepage: "https://www.trapets.com",
        details: [
          "Corp. Reg. No. 556586-4773",
          "EU VAT SE556586477301",
          "Kungsgatan 56, SE-111 22 Stockholm, Sweden",
        ],
        links: [
          // Trapets screening runs on Instantwatch; its only status info is the
          // production API health endpoint (returns {"Status":"Ok"}).
          { kind: "status", href: "https://kyc.instantwatch.net/status" },
          {
            kind: "security",
            href: "https://www.trapets.com/trust-centre/information-security-and-privacy",
          },
        ],
      }}
      footnote={t.pricesFootnote}
      error={error}
      success={success}
      control={
        <div className="flex flex-col items-end gap-1">
          <Label htmlFor="trapets-mode" className="sr-only">
            {t.mode}
          </Label>
          <ToggleGroup
            id="trapets-mode"
            type="single"
            value={mode}
            onValueChange={handleModeChange}
            disabled={isPending}
            className="w-fit"
          >
            <ToggleGroupItem value="disabled">{t.disabled}</ToggleGroupItem>
            <ToggleGroupItem value="test">{t.test}</ToggleGroupItem>
            <ToggleGroupItem value="production">{t.production}</ToggleGroupItem>
          </ToggleGroup>
          <p className="text-xs text-muted-foreground text-right max-w-[16rem]">{modeHint}</p>
        </div>
      }
    />
  );
}
